import { FormDialog, FormDialogWidgetProps, useMelioIntl } from '@melio/ar-domain';
import { Form, useMelioForm } from '@melio/penny';
import { forwardRef } from '@melio/platform-utils';
import { object, SchemaOf, string } from 'yup';

import { CustomerFormFields } from './types';

const useSchema = () => {
  const { formatMessage } = useMelioIntl();

  return object().shape({
    customerName: string()
      .min(2, formatMessage('ar.invoiceLifecycle.activities.createCustomer.form.customerName.min.label'))
      .required(formatMessage('ar.invoiceLifecycle.activities.createCustomer.form.customerName.required.label')),
    email: string()
      .email(formatMessage('ar.invoiceLifecycle.activities.createCustomer.form.email.validation.label'))
      .required(formatMessage('ar.invoiceLifecycle.activities.createCustomer.form.email.required.label')),
  }) as SchemaOf<CustomerFormFields>;
};

export type CustomerFormDialogProps = FormDialogWidgetProps<
  CustomerFormFields,
  {
    header: string;
    primaryButtonLabel: string;
    secondaryButtonLabel: string;
  }
>;

export const CustomerFormDialog = forwardRef<CustomerFormDialogProps>(
  ({ onSubmit, defaultValues, isSaving, primaryButtonLabel, secondaryButtonLabel, ...props }, ref) => {
    const { registerField, ...useFormResults } = useMelioForm({
      schema: useSchema(),
      onSubmit,
      defaultValues,
      isSaving,
      subscribeToDefaultValuesChanges: true,
    });
    const { formatMessage } = useMelioIntl();

    return (
      <FormDialog
        useFormResults={useFormResults}
        size="small"
        primaryButton={{ label: primaryButtonLabel }}
        secondaryButton={{ label: secondaryButtonLabel }}
        {...props}
        ref={ref}
      >
        <Form.TextField
          {...registerField('customerName')}
          labelProps={{ label: formatMessage('ar.invoiceLifecycle.activities.createCustomer.form.customerName.label') }}
        />
        <Form.TextField
          {...registerField('email')}
          labelProps={{ label: formatMessage('ar.invoiceLifecycle.activities.createCustomer.form.email.label') }}
        />
      </FormDialog>
    );
  }
);
